import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { Navbar } from '@/components/Navbar';
import { SiteAds } from '@/components/ads/AdSlot';
import { BootLoader } from '@/components/capsule/BootLoader';
import { CapsulePageLoader } from '@/components/capsule/CapsulePageLoader';
import { Toaster } from '@/components/ui/sonner';
import { lazy, Suspense, memo } from 'react';
import '@/App.css';

// Eagerly load landing page for fast initial render
import LandingPage from '@/pages/LandingPage';

// Lazy load other pages
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const LibraryPage = lazy(() => import('@/pages/LibraryPage'));
const SeriesPage = lazy(() => import('@/pages/SeasonPage'));
const VideoPlayerPage = lazy(() => import('@/pages/VideoPlayerPage'));
const GlobalChatPage = lazy(() => import('@/pages/GlobalChatPage'));
const ChatRoomsPage = lazy(() => import('@/pages/ChatRoomsPage'));
const ChannelAnalyticsPage = lazy(() => import('@/pages/ChannelAnalyticsPage'));
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const UserManagementPage = lazy(() => import('@/pages/UserManagementPage'));
const LeaderboardPage = lazy(() => import('@/pages/LeaderboardPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const PublicProfilePage = lazy(() => import('@/pages/PublicProfilePage'));
const DMPage = lazy(() => import('@/pages/DMPage'));
const FriendRequestsPage = lazy(() => import('@/pages/FriendRequestsPage'));
const GroupChatsPage = lazy(() => import('@/pages/GroupChatsPage'));
const NewsletterPage = lazy(() => import('@/pages/NewsletterPage'));
const ShopPage = lazy(() => import('@/pages/ShopPage'));
const InventoryPage = lazy(() => import('@/pages/InventoryPage'));
const TransactionHistoryPage = lazy(() => import('@/pages/TransactionHistoryPage'));
const GamesPage = lazy(() => import('@/pages/GamesPage'));
const OwnerReportsPage = lazy(() => import('@/pages/OwnerReportsPage'));

// Lazy load heavy components
const PWAInstallPrompt = lazy(() => import('@/components/PWAInstallPrompt'));
const FloatingChat = lazy(() => import('@/components/FloatingChat'));
const DragonBallCollector = lazy(() => import('@/components/DragonBallCollector'));
const DragonRadarHost = lazy(() => import('@/components/radar/DragonRadarHost'));
const GhostHunter = lazy(() => import('@/components/halloween/GhostHunter'));
const IncomingCallHandler = lazy(() => import('@/components/IncomingCallHandler'));
const DiscordLinkNotification = lazy(() => import('@/components/DiscordLinkNotification'));

// Loading component for route chunks
const PageLoader = memo(() => <CapsulePageLoader />);

// Wrapper component to conditionally render FloatingChat
const ConditionalFloatingChat = memo(() => {
  const location = useLocation();
  // Hide floating chat on channels pages since it's redundant
  const isChannelsPage = location.pathname.startsWith('/channels');
  
  if (isChannelsPage) return null;
  return (
    <Suspense fallback={null}>
      <FloatingChat />
    </Suspense>
  );
});

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <CapsulePageLoader />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return children;
};

const AppContent = () => {
  return (
    <div className="App">
      <Navbar />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route
            path="/library"
            element={
              <ProtectedRoute>
                <LibraryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/series/:seriesId"
            element={
              <ProtectedRoute>
                <SeriesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/series/standalone/:seasonId"
            element={
              <ProtectedRoute>
                <SeriesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/watch/:episodeId"
            element={
              <ProtectedRoute>
                <VideoPlayerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <GlobalChatPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/channels"
            element={
              <ProtectedRoute>
                <ChatRoomsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/channels/:roomSlug"
            element={
              <ProtectedRoute>
                <ChatRoomsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leaderboard"
            element={
              <ProtectedRoute>
                <LeaderboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <UserManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/:userId"
            element={
              <ProtectedRoute>
                <PublicProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dm/:userId"
            element={
              <ProtectedRoute>
                <DMPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/friends"
            element={
              <ProtectedRoute>
                <FriendRequestsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/groups"
            element={
              <ProtectedRoute>
                <GroupChatsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/groups/:groupId"
            element={
              <ProtectedRoute>
                <GroupChatsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/channel-analytics"
            element={
              <ProtectedRoute>
                <ChannelAnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/news"
            element={
              <ProtectedRoute>
                <NewsletterPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/shop"
            element={
              <ProtectedRoute>
                <ShopPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory"
            element={
              <ProtectedRoute>
                <InventoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transactions"
            element={
              <ProtectedRoute>
                <TransactionHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/games"
            element={<GamesPage />}
          />
          <Route
            path="/owner/reports"
            element={
              <ProtectedRoute>
                <OwnerReportsPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
      <SiteAds />
      <Toaster position="top-right" />
      <Suspense fallback={null}>
        <PWAInstallPrompt />
      </Suspense>
      <ConditionalFloatingChat />
      <Suspense fallback={null}>
        <DragonBallCollector />
        <DragonRadarHost />
        <GhostHunter />
      </Suspense>
      <Suspense fallback={null}>
        <IncomingCallHandler />
      </Suspense>
      <Suspense fallback={null}>
        <DiscordLinkNotification />
      </Suspense>
      <AppBootLoader />
    </div>
  );
};

const AppBootLoader = () => {
  const { loading } = useAuth();
  return <BootLoader ready={!loading} />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ThemeProvider>
          <NotificationProvider>
            <AppContent />
          </NotificationProvider>
        </ThemeProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
